import {
  Control,
  Controller,
  FieldErrors,
  useFieldArray,
  UseFormWatch,
} from 'react-hook-form';
import { MouseEvent } from 'react';
import { UserFormData } from '../../add-user-dialog/user-form-page';

import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import SharedProfileInformationAccordion from '../../../../shared/shared-profile-information-accordion';
import SharedProfileInformationItemActions from '../../../../shared/shared-profile-information-item-actions';

interface ImsAccordionProps {
  control: Control<UserFormData>;
  errors: FieldErrors<UserFormData>;
  watch: UseFormWatch<UserFormData>;
  disabled: boolean;
}

export default function ImsAccordion({
  control,
  errors,
  disabled,
  watch,
}: ImsAccordionProps) {
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'ims',
  });

  const menuItems = [
    { value: 'custom', label: 'Custom' },
    { value: 'home', label: 'Home' },
    { value: 'other', label: 'Other' },
    { value: 'work', label: 'Work' },
  ];

  const menuItemsTwo = [
    { value: 'aim', label: 'AOL Instant Messenger' },
    { value: 'custom_protocol', label: 'Custom' },
    { value: 'gtalk', label: 'Google Talk' },
    { value: 'icq', label: 'ICQ' },
    { value: 'jabber', label: 'Jabber' },
    { value: 'msn', label: 'MSN' },
    { value: 'net_meeting', label: 'Net Meeting' },
    { value: 'qq', label: 'QQ' },
    { value: 'skype', label: 'Skype' },
    { value: 'yahoo', label: 'Yahoo' },
  ];

  const imsErrors = errors['ims'];
  const imsCount = fields.length;

  const handleOnAdd = (
    event: MouseEvent<HTMLSpanElement, globalThis.MouseEvent>
  ) => {
    event.stopPropagation();
    append({
      type: '',
      customType: '',
      protocol: '',
      customProtocol: '',
      im: '',
      primary: false,
    });
  };

  return (
    <SharedProfileInformationAccordion
      title="Instant Messaging"
      count={imsCount}
      hasError={!!imsErrors}
      onAdd={handleOnAdd}
      disabled={disabled}
    >
      {fields.map((item, index) => {
        const typeField = watch(`ims.${index}.type`);

        return (
          <Box key={item.id}>
            <Stack
              gap={1}
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ my: 1.5 }}
            >
              <Stack flexDirection="row" alignItems="center" gap={2}>
                <Controller
                  rules={{ required: 'This field is required.' }}
                  name={`ims.${index}.type`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required
                      inputRef={field.ref}
                      inputProps={{
                        id: `ims-type-${index}`,
                      }}
                      InputLabelProps={{
                        htmlFor: `ims-type-${index}`,
                      }}
                      name="ims"
                      select
                      disabled={disabled}
                      label="Type"
                      error={!!imsErrors?.[index]?.type}
                      helperText={
                        imsErrors?.[index]?.type
                          ? 'This field is required.'
                          : ''
                      }
                      sx={{
                        minWidth: 150,
                      }}
                    >
                      {menuItems.map((item) => (
                        <MenuItem value={item.value} key={item.label}>
                          {item.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
                {typeField === 'custom' && (
                  <Controller
                    rules={{ required: 'This field is required.' }}
                    name={`ims.${index}.customType`}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        required
                        inputRef={field.ref}
                        inputProps={{
                          id: `ims-customType-${index}`,
                        }}
                        InputLabelProps={{
                          htmlFor: `ims-customType-${index}`,
                        }}
                        disabled={disabled}
                        name="ims"
                        label="Custom Type"
                        error={!!imsErrors?.[index]?.customType}
                        helperText={
                          imsErrors?.[index]?.customType?.message
                            ? imsErrors?.[index]?.customType?.message
                            : ''
                        }
                        sx={{
                          minWidth: 225,
                        }}
                      />
                    )}
                  />
                )}
                <Controller
                  name={`ims.${index}.protocol`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      inputRef={field.ref}
                      inputProps={{
                        id: `ims-protocol-${index}`,
                      }}
                      InputLabelProps={{
                        htmlFor: `ims-protocol-${index}`,
                      }}
                      value={field.value || ''}
                      name={'ims'}
                      select
                      disabled={disabled}
                      label="Protocol"
                      error={!!imsErrors?.[index]?.protocol}
                      helperText={
                        imsErrors?.[index]?.protocol?.message
                          ? imsErrors?.[index]?.protocol?.message
                          : ''
                      }
                      sx={{
                        minWidth: 150,
                      }}
                    >
                      {menuItemsTwo.map((item) => (
                        <MenuItem value={item.value} key={item.label}>
                          {item.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />

                <Controller
                  rules={{ required: 'This field is required.' }}
                  name={`ims.${index}.im`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required
                      inputRef={field.ref}
                      error={!!imsErrors?.[index]?.im}
                      helperText={
                        imsErrors?.[index]?.im?.message
                          ? imsErrors?.[index]?.im?.message
                          : ''
                      }
                      name="ims"
                      disabled={disabled}
                      autoComplete="off"
                      label={`Im`}
                      sx={{
                        minWidth: 225,
                      }}
                    />
                  )}
                />
              </Stack>

              <SharedProfileInformationItemActions
                index={index}
                totalCount={imsCount}
                disabled={disabled}
                onMoveDown={() => move(index, index + 1)}
                onMoveUp={() => move(index, index - 1)}
                onDelete={() => remove(index)}
              />
            </Stack>
            {index !== fields.length - 1 && <Divider />}
          </Box>
        );
      })}
    </SharedProfileInformationAccordion>
  );
}
